import { Box, Grid, SelectChangeEvent } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  ICandidate,
  IJob,
  IJobCandidateInteraction,
  amplitude,
  ICandidateRecentConversation,
  ICandidateSubmissionSearchFilterVM,
  ICandidateFilter,
} from 'wisestep-api';
import {
  WsPagination,
  ScrollableTabs,
  TabTextVariant,
  WsListingLoader,
  IFilterList,
  WsFilterTags,
  IUserDTO,
  WsColors,
} from 'wisestep-base';

import BulkEmail from '../../../components/BulkEmail';
import BulkSmsDrawer from '../../../components/BulkSms/BulkSmsDrawer';
import WsJobsCrmPipelineAdvanceFiltersNew from '../../../components/filters/jobsCrmPipeline/WsJobsCrmPipelineAdvanceFiltersNew';
import JobsCrmPipelineAdvanceFiltersFormProps from '../../../components/filters/models/JobsCrmPipelineAdvanceFiltersFormProps';
import NoDataFoundCard from '../../../components/noDataFoundCard/NoDataFoundCard';
import { WsCandidateCard } from '../../../components/WsCandidateCard';
import { useUserContext } from '../../../context';
import conversationWidgetStore from '../../../features/conversationWidget/store/conversationWidgetStore';
import { ITrackCvPages } from '../../../features/conversationWidget/utils/cvHelper';
import { handleCandidateDownload } from '../../../features/dataDownload/helper/PayloadHelper';
import Filters from '../../../features/submissionsNew/components/filters';
import AdvancedFilters from '../../../features/submissionsNew/components/filters/advanced';
import ApplyedFilter from '../../../features/submissionsNew/components/filters/smart/ApplyedFilter';
import useSubmissionsNewContext from '../../../features/submissionsNew/components/hooks/useSubmissionsNewContext';
import {
  defaultSubmissionsListingSearchQuery,
  getDefaultFilterValues,
  ISubmissionAdvanceFilters,
  processUserInputsToFilters,
} from '../../../features/submissionsNew/utils/Helper';
import ErrorLogger from '../../../providers/ErrorLogger';
import { cacheJob } from '../../../utils/cacheService';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { paginationSettingsStore } from '../../../utils/PaginationSettingStore';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import CandidateBulkSelectionBar from '../../candidates/components/candidateBulkSelectionBar';
import MappingCandidateCard from '../../candidates/mappings/components/mappingCard';
import TalentPoolCandidateCard from '../../candidates/talentpool/components/candidateCard/TalentPoolCandidateCard';
import { TalentPoolSearchRequest } from '../../candidates/talentpool/components/search/TalentPoolSearchRequest';
import { getInteraction } from '../../candidates/utils/CandidateHelper';
import { jobCrmStatesCount } from '../../jobs/listing/JObSearchFilterDefaultQuery';
import useCvViews from '../../submissions/hooks/useCvViews';
import useListingCandidates from '../../submissions/hooks/useListingCandidates';
import ShareCandidateProfileEmail from '../../submissions/submissionListing/components/ShareCandidateProfileEmail';
import { pipelineEmptyValues } from '../components/helper/JobCrmEmptyValues';
import {
  generateJobCrmBackwardURL,
  getDeafaultCandidateSearchTypeList,
  getDefaultCandidateSearchType,
  getDefaultCandidateType,
  JobsCrm,
  pipelineTabsList,
  pushPipelineFilters,
} from '../components/helper/JobsCrmHelper';
import { JobsCrmDefaultRequest } from '../components/requests/JobsCrmDefaultRequest';
import JobsCrmSearch from '../components/search';
import {
  amplitudeEventJobCrmPipelineApplication,
  amplitudeEventJobCrmPipelineApplicationsSelectUnselect,
  amplitudeEventJobCrmPipelineMappings,
  amplitudeEventJobCrmPipelineMappingsSelectUnselect,
  amplitudeEventJobCrmPipelineSubmissions,
  amplitudeEventJobCrmPipelineSubmissionsSelectUnselect,
  amplitudeEventJobCrmPipelineUploaded,
  amplitudeEventJobCrmPipelineUploadedSelectUnselect,
  amplitudeEventJobCrmTabs,
} from '../utils/AmplitudeEvents';

interface JobsCrmPipelineProps {
  index?: number;
  job: IJob;
  candidateEmail?: string;
  jobStats?: jobCrmStatesCount;
}
const JobsCrmPipeline = ({ index, job, candidateEmail, jobStats }: JobsCrmPipelineProps) => {
  const paginationData = paginationSettingsStore((state) => state.getPaginationSettings);
  const paginationSettings = paginationData();
  const [openBulkSms, setOpenBulkSms] = useState<boolean>(false);
  const [openEmail, setOpenEmail] = useState<boolean>(false);
  const defaultRequest = { ...JobsCrmDefaultRequest };
  const [cvIndex, setCvIndex] = useState<number | null>(null);
  const openModal = useConversationWidgetStore((state) => state.setCandidate);
  const updateView = useConversationWidgetStore((state) => state.updateView);
  const updateSelectedJob = conversationWidgetStore((state) => state.updateSelectedJob);
  const [tabIndex, setTabIndex] = useState<number>(index ?? 0);
  defaultRequest.jobId = job.id ?? '';
  defaultRequest.jobCode = job.jobCode ?? '';
  defaultRequest.jobTitle = job?.title ?? '';
  defaultRequest.jobSkills = job?.skills ?? [];
  defaultRequest.jobMatchingContent = [job?.title ?? '', job?.description ?? ''];
  defaultRequest.candidateSearchType = getDefaultCandidateSearchType(index ?? 0);
  defaultRequest.candidateEmail = candidateEmail ?? '';
  defaultRequest.candidateSearchTypeList = getDeafaultCandidateSearchTypeList(index ?? 0);
  // index !== 3 ? defaultRequest.candidateSearchTypeList : ['uploaded'];
  const jobIdFilter: ICandidateFilter = {
    filterName: 'JOB_ID',
    values: [job.id ?? ''],
  };
  const [type, setType] = useState(getDefaultCandidateType(index ?? 0));
  const [query, setQuery] = useState<IJobCandidateInteraction>(defaultRequest);
  const [loader, setLoader] = useState<boolean>(false);
  const [advancedFilterDefaultValues, setAdvancedFilterFields] =
    useState<JobsCrmPipelineAdvanceFiltersFormProps>(pipelineEmptyValues);
  const setSearchQuery = useConversationWidgetStore((state) => state.setSearchQuery);
  const [appliedFilters, setAppliedFilters] = useState<IFilterList[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [numberOfSelectedCandidates, setNumberOfSelectedCandidates] = useState<number>(0);
  const pageChange = (event, value) => {
    if (type === 'submissions') {
      const updatedQuery = { ...searchSubmissionQuery };
      updatedQuery.page = value;
      setSubmissionQuery(updatedQuery);
      setPageNumber(value + 1);
    } else {
      const updatedQuery = { ...query };
      updatedQuery.page = value;
      setQuery(updatedQuery);
      setPageNumber(value + 1);
    }
  };
  const [searchSubmissionQuery, setSubmissionQuery] = useState<ICandidateSubmissionSearchFilterVM>({
    ...defaultSubmissionsListingSearchQuery,
    filters: [jobIdFilter],
    size: Number(paginationSettings?.jobCrmPagination ?? 20),
  });
  const [openAdvancedFilters, setOpenAdvancedFilters] = useState<boolean>(false);

  const [advancedFilterDefaults, setAdvancedFilterDefaults] = useState<ISubmissionAdvanceFilters>(
    getDefaultFilterValues(searchSubmissionQuery)
  );
  const setIsWidgetOpen = useConversationWidgetStore((state) => state.setIsWidgetOpen);
  const navigationState = useConversationWidgetStore((state) => state.navigationState);
  const updateNavigationState = useConversationWidgetStore((state) => state.updateNavigationState);
  const navigate = useConversationWidgetStore((state) => state.navigate);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const data = useListingCandidates(query, setLoader, 'jobCRM');
  const { submissions } = useSubmissionsNewContext(searchSubmissionQuery, true);
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const cvViewsData = useCvViews(query, data.list, loggedInUser?.email ?? '');
  const [openShareCandidateEmail, setOpenShareCandidateEmail] = useState<boolean>(false);
  const [selectedCandidateEntitys, setSelectedCandidateEntitys] = useState<
    ICandidateRecentConversation[]
  >([]);
  const [candidateToShare, setCandidateToShare] = useState<ICandidate | null>();
  const [candidateList, setCandidateList] = useState<(ICandidate & { isSelected: boolean })[]>([]);

  useEffect(() => {
    const submissionsList = submissions?.content?.map((candidate) => ({
      ...candidate,
      isSelected: false,
    })) as (ICandidate & { isSelected: boolean })[];
    setCandidateList(submissionsList);
  }, [submissions]);

  useEffect(() => {
    if (navigationState && navigate && cvIndex !== null) {
      if (cvIndex > 0 && cvIndex <= data.list.length - 1 && navigate == 'prev') {
        const index = cvIndex - 1;
        type === 'submissions' ? updateView('submission') : updateView('mapping');
        setCvIndex(index);
        openModal(data.list[index]);
      } else if (cvIndex >= 0 && cvIndex < data.list.length - 1 && navigate == 'next') {
        const index = cvIndex + 1;
        type === 'submissions' ? updateView('submission') : updateView('mapping');
        setCvIndex(index);
        openModal(data.list[index]);
      } else {
        setIsWidgetOpen(false);
      }
      updateNavigationState(false);
    }
  }, [
    cvIndex,
    data.list,
    navigate,
    navigationState,
    openModal,
    setIsWidgetOpen,
    type,
    updateNavigationState,
    updateView,
  ]);

  useEffect(() => {
    switch (index) {
      case 0:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_MAPPINGS);
        break;
      case 1:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_SUBMISSION);
        break;
      case 2:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_APPLICATION);
        break;
      case 3:
        setCvViewSourcePage(ITrackCvPages.JOBCRM_UPLOAD);
        break;
    }
    sessionStorage.setItem('jobCrmUrl', jobCrmUrl);
  }, [index, setCvViewSourcePage]);

  const updateAdvancedQuery = (data: JobsCrmPipelineAdvanceFiltersFormProps, reset = false) => {
    const updatedQuery = { ...query };
    [
      'emailAvailability',
      'phoneAvailability',
      'commPreferenceEmail',
      'commPreferencePhone',
    ].forEach((field) => delete updatedQuery[field]);
    updatedQuery.page = 0;
    updatedQuery.radiusInMiles = undefined;
    updatedQuery.sources = data.source ?? [];
    updatedQuery.tags = data.tags ?? [];
    updatedQuery.workAuthorizationCategories = [];
    data.workAuthorization?.forEach((obj) => {
      updatedQuery.workAuthorizationCategories?.push(obj);
    });
    updatedQuery.minSalary = data.salaryRange?.min ?? 0;
    updatedQuery.maxSalary = data.salaryRange?.max ?? 0;
    updatedQuery.salaryCurrency = data.salaryRange?.currency ?? '';
    updatedQuery.salaryTimeSpan = data.salaryRange?.unitTime ?? null;
    updatedQuery.noticePeriodMaxDays = data.noticePeriod?.max ?? 0;
    updatedQuery.noticePeriodMinDays = data.noticePeriod?.min ?? 0;
    updatedQuery.relocationPreference = data.relocationPreferences ?? null;
    if (data.currentStageCurrentStatusPairs) {
      const stageStstusObj = JSON.parse(data.currentStageCurrentStatusPairs);
      updatedQuery.currentStageCurrentStatusPairs = stageStstusObj.stageStatusData;
      updatedQuery.candidateJobStatus =
        stageStstusObj.jobOption === 'open_jobs'
          ? 'Opened'
          : stageStstusObj.jobOption === 'closed_jobs'
          ? 'Closed'
          : 'Any';
    } else {
      updatedQuery.currentStageCurrentStatusPairs = [];
      updatedQuery.candidateJobStatus = 'Any';
    }

    data.commPreferences?.forEach((value) => {
      if (value == 'Email') updatedQuery.commPreferenceEmail = true;
      else if (value == 'Phone') updatedQuery.commPreferencePhone = true;
    });
    data.contactAvailability?.forEach((value) => {
      if (value == 'Email') updatedQuery.emailAvailability = true;
      else if (value == 'Phone') updatedQuery.phoneAvailability = true;
      else if (value == 'Both') {
        updatedQuery.phoneAvailability = true;
        updatedQuery.emailAvailability = true;
      }
    });
    if (reset) {
      updatedQuery.candidatesName = [];
      updatedQuery.candidatesEmail = [];
      updatedQuery.candidatesPhoneNumber = [];
      updatedQuery.query = '';
    }
    updatedQuery.createdByRecruiter = data.createdBy ?? [];
    return updatedQuery;
  };

  const search = (data: TalentPoolSearchRequest) => {
    setSearchQuery(data.query);
    const updatedQuery = { ...query };
    updatedQuery.page = 0;
    updatedQuery.query = data.query;
    updatedQuery.candidateLocations = data.locations;
    updatedQuery.radiusInMiles = data.distance;
    updatedQuery.recruiterActions = data.recruiterActions;
    updatedQuery.candidateResponses = data.candidateResponses;
    updatedQuery.searchWithMostRecentConversation = data.searchWithMostRecentConversation;
    updatedQuery.minMatchScore = data.minMatchScore;
    if (isEmpty(data?.query)) {
      updatedQuery.candidatesName = [];
      updatedQuery.candidatesEmail = [];
      updatedQuery.candidatesPhoneNumber = [];
    }
    if (data?.searchBy === 'Name') {
      updatedQuery.candidatesName = [data?.query];
    } else if (data?.searchBy === 'Email') {
      updatedQuery.candidatesEmail = [data?.query];
    } else if (data?.searchBy === 'Phone No') {
      updatedQuery.candidatesPhoneNumber = [data?.query];
    }

    if (data.sort) updatedQuery.sort = [data.sort];
    setQuery(updatedQuery);
    amplitude('JobCRM_Pipeline_search_button', {
      userId: loggedInUser.email,
      clickedOn: 'Search',
      pageName: 'JobCrm',
      pageUrl: window.location.href,
    });
    setPageNumber(1);
  };

  const onCVClick = (index: number) => {
    if (job) {
      cacheJob(job);
      updateSelectedJob(job);
    }
    type === 'submissions' ? updateView('submission') : updateView('mapping');
    setCvIndex(index);
    // openModal(data.list[index]);
  };

  const onDeleteFilterTag = (tag: string) => {
    setAppliedFilters((prev) => {
      const idOfChip = prev.find((existChip) => existChip.tag === tag)?.id ?? '';
      const advancedFilterDefaults: Partial<JobsCrmPipelineAdvanceFiltersFormProps> =
        advancedFilterDefaultValues;
      advancedFilterDefaults[idOfChip] = pipelineEmptyValues[idOfChip];
      if (tag.match('Business Definition')) {
        advancedFilterDefaults['currentStageCurrentStatusPairs'] =
          pipelineEmptyValues['currentStageCurrentStatusPairs'];
        advancedFilterDefaults['candidateJobStatus'] = pipelineEmptyValues['candidateJobStatus'];
      }
      setAdvancedFilterFields((prev) => ({ ...prev, ...advancedFilterDefaults }));
      setQuery(updateAdvancedQuery(advancedFilterDefaults));
      const index = prev.findIndex((existChip) => existChip.id === idOfChip);
      prev.splice(index, 1);
      return [...prev];
    });
  };
  const onApplyFilters = (data: JobsCrmPipelineAdvanceFiltersFormProps, reset = false) => {
    setAdvancedFilterFields(data);
    setPageNumber(1);
    setQuery(updateAdvancedQuery(data, reset));
    setAppliedFilters(pushPipelineFilters(data as JobsCrmPipelineAdvanceFiltersFormProps));
  };
  const jobCrmUrl = `/jobs-crm/${job.id}/false/1/pipeline`;
  const ChangeIndex = {
    [0]: () => {
      setTabIndex(0);
      unselectAllCandidates();
      pageChange(null, 0);
      setCvIndex(null);
      window.history.replaceState(null, '', `/jobs-crm/${job.id}/false/0/pipeline`);
      const updatedQuery = { ...query };
      updatedQuery.candidateSearchType = 'OnlyMappings';
      updatedQuery.candidateSearchTypeList = ['mapped'];
      setCvViewSourcePage(ITrackCvPages.JOBCRM_MAPPINGS);
      setQuery(updatedQuery);
      setType('mappings');
      amplitudeEventJobCrmTabs['mappings'](loggedInUser);
    },
    [1]: () => {
      setTabIndex(1);
      unselectAllCandidates();
      pageChange(null, 0);
      setCvIndex(null);
      window.history.replaceState(null, '', jobCrmUrl);
      // const updatedQuery = { ...query };
      // updatedQuery.candidateSearchType = 'OnlySubmissions';
      // updatedQuery.recruiterActions = [];
      // updatedQuery.candidateResponses = [];
      // updatedQuery.candidateSearchTypeList = defaultRequest.candidateSearchTypeList;
      setCvViewSourcePage(ITrackCvPages.JOBCRM_SUBMISSION);
      const updatedQuery = { ...defaultSubmissionsListingSearchQuery };
      updatedQuery.size = Number(paginationSettings?.jobCrmPagination ?? 20);
      updatedQuery.filters = [jobIdFilter];
      if (updatedQuery.filters) {
        updatedQuery.filters = [jobIdFilter];
      } else {
        updatedQuery.filters = [jobIdFilter];
      }

      setSubmissionQuery({ ...updatedQuery });
      setType('submissions');
      amplitudeEventJobCrmTabs['submissions'](loggedInUser);
    },
    [2]: () => {
      setTabIndex(2);
      unselectAllCandidates();
      pageChange(null, 0);
      setCvIndex(null);
      window.history.replaceState(null, '', `/jobs-crm/${job.id}/false/2/pipeline`);
      const updatedQuery = { ...query };
      updatedQuery.candidateSearchType = 'OnlyMappings';
      updatedQuery.recruiterActions = [];
      updatedQuery.candidateResponses = [];
      updatedQuery.candidateSearchTypeList = ['applied'];
      updatedQuery.page = 0;
      setCvViewSourcePage(ITrackCvPages.JOBCRM_APPLICATION);
      setQuery(updatedQuery);
      setType('Applications');
      amplitudeEventJobCrmTabs['Applications'](loggedInUser);
    },
    [3]: () => {
      setTabIndex(3);
      unselectAllCandidates();
      pageChange(null, 0);
      setCvIndex(null);
      window.history.replaceState(null, '', `/jobs-crm/${job.id}/false/3/pipeline`);
      const updatedQuery = { ...query };
      updatedQuery.candidateSearchType = 'OnlyMappings';
      updatedQuery.candidateSearchTypeList = ['uploaded'];
      updatedQuery.page = 0;
      updatedQuery.recruiterActions = [];
      updatedQuery.candidateResponses = [];
      setCvViewSourcePage(ITrackCvPages.JOBCRM_UPLOAD);
      setQuery(updatedQuery);
      setType('uploaded');
      amplitudeEventJobCrmTabs['uploaded'](loggedInUser);
    },
  };

  const onSort = (event: SelectChangeEvent) => {
    query.sort = [event.target.value ?? 'createdOn,desc'];
    setQuery({ ...query });
  };
  const addSelectedCandidate = (candidate: ICandidate & { isSelected: boolean }) => {
    if (type === 'submissions') {
      const candidateIndex = candidateList.findIndex((c) => c.id === candidate.id);
      if (candidateIndex === -1) return;
      const updatedCandidatesList = [...candidateList];
      updatedCandidatesList[candidateIndex] = {
        ...candidateList[candidateIndex],
        isSelected: !candidateList[candidateIndex].isSelected,
      };
      setNumberOfSelectedCandidates((prevCount) =>
        updatedCandidatesList[candidateIndex].isSelected ? prevCount + 1 : prevCount - 1
      );
      setCandidateList(updatedCandidatesList);
    } else {
      setNumberOfSelectedCandidates((prevCount) =>
        candidate.isSelected ? prevCount - 1 : prevCount + 1
      );
      candidate.isSelected = !candidate.isSelected;
    }
  };

  const selectAllCandidates = () => {
    if (type === 'submissions') {
      selectCandidates(candidateList);
    } else {
      selectCandidates(data.list);
    }
  };

  const selectCandidates = (candidates) => {
    const selectedCandidates = candidates.filter(
      (candidate) => candidate.email && !candidate.isInDnc
    );
    selectedCandidates.forEach((candidate) => (candidate.isSelected = true));
    setNumberOfSelectedCandidates(selectedCandidates.length);
  };

  const unselectCandidates = (candidates) => {
    candidates.forEach((candidate) => {
      candidate.isSelected = false;
    });
    setNumberOfSelectedCandidates(0);
  };

  const unselectAllCandidates = () => {
    if (type === 'submissions') {
      unselectCandidates(candidateList);
    } else {
      unselectCandidates(data.list);
    }
  };

  const handleDownload = async (downloadType: string) => {
    const payload = {
      ...query,
      ...(type === 'submissions' && {
        sort: [],
        filters: [
          {
            filterName: 'JOB_ID',
            values: [query.jobId],
            fields: {},
          },
        ],
      }),
    };
    const downloadStatus = await handleCandidateDownload(downloadType, payload, [], type);
    downloadStatus == 'success'
      ? setNotification(success('Downloaded candidates'))
      : setNotification(warning('Something went wrong, Please try again.'));
  };

  const amplitudeEventHandlerPipelineTabs = {
    ['submissions']: (eventType: string) =>
      amplitudeEventJobCrmPipelineSubmissionsSelectUnselect[eventType](loggedInUser),
    ['mappings']: (eventType: string) =>
      amplitudeEventJobCrmPipelineMappingsSelectUnselect[eventType](loggedInUser),
    ['Applications']: (eventType: string) =>
      amplitudeEventJobCrmPipelineApplicationsSelectUnselect[eventType](loggedInUser),
    ['uploaded']: (eventType: string) =>
      amplitudeEventJobCrmPipelineUploadedSelectUnselect[eventType](loggedInUser),
  };

  const onShareEmailClick = () => {
    setCandidateToShare(null);
    setOpenShareCandidateEmail(true);
    const selectedEntitys = candidateList
      .map((candidate, index) => ({ isSelected: candidate.isSelected, index: index }))
      .filter((candidate) => candidate.isSelected)
      .map((item) => data.stageStatus[item.index]);

    setSelectedCandidateEntitys(selectedEntitys);
  };

  const onDeleteSubmissionFilterTag = (data) => {
    setOpenAdvancedFilters(false);

    const queryUpdate =
      data.split(' : ')[0] === 'SubmittedOrUpdatedbetween'
        ? 'SUBMITTED_OR_UPDATED_BETWEEN'
        : data.split(' : ')[0];
    const keyToRemove = data.split(' : ')[0] === 'SALARY' ? 'salaryRange' : data.split(' : ')[0];
    const updatedFilters = Object.keys(advancedFilterDefaults).reduce((result, key) => {
      if (key === keyToRemove) {
        if (keyToRemove === 'salaryRange') {
          result[key] = null;
        } else if (keyToRemove === 'SubmittedOrUpdatedbetween') {
          result[key] = '';
          result['SubmittedOrUpdatedFromDate'] = '';
          result['SubmittedOrUpdatedToDate'] = '';
        }
      } else if (
        !(
          (key === 'SubmittedOrUpdatedFromDate' || key === 'SubmittedOrUpdatedToDate') &&
          keyToRemove === 'SubmittedOrUpdatedbetween'
        ) &&
        !(
          (key === 'stageDateFromDate' || key === 'stageDateToDate') &&
          keyToRemove === 'STAGE_STATUS_BETWEEN'
        )
      ) {
        result[key] = advancedFilterDefaults[key];
      }
      return result;
    }, {} as ISubmissionAdvanceFilters);
    setAdvancedFilterDefaults(updatedFilters);
    const updatingFilters = searchSubmissionQuery.filters.filter(
      (filters) => filters.filterName !== queryUpdate
    );
    setSubmissionQuery({ ...searchSubmissionQuery, filters: updatingFilters });
  };

  const onFormSubmit = (data: ISubmissionAdvanceFilters) => {
    setOpenAdvancedFilters(false);
    const processedFilters = isEmpty(data) ? [] : processUserInputsToFilters(data);
    const updatedQuery = {
      ...searchSubmissionQuery,
      filters: [...processedFilters, jobIdFilter],
      page: 0,
    };
    setPageNumber(0);
    if (isEmpty(data)) {
      setSubmissionQuery({ ...defaultSubmissionsListingSearchQuery, filters: [jobIdFilter] });
      setAdvancedFilterDefaults({} as ISubmissionAdvanceFilters);
    } else {
      setAdvancedFilterDefaults(data);
      setSubmissionQuery(updatedQuery);
    }
  };
  const onClear = () => {
    const query = { ...defaultSubmissionsListingSearchQuery, filters: [jobIdFilter] };
    query.query = '';
    setSubmissionQuery(query);
    setAdvancedFilterDefaults({} as ISubmissionAdvanceFilters);
  };
  return (
    <Box>
      <Grid container display="flex" justifyContent="space-between">
        <Grid item xs={9}>
          <Box>
            {type === 'submissions' ? (
              <Filters
                onAdvancedFiltersClick={() => setOpenAdvancedFilters(!openAdvancedFilters)}
                searchQuery={searchSubmissionQuery}
                getUpdatedFiltersFromFilters={(query) => setSubmissionQuery(query)}
                selectedCandidates={[]}
                updateUI={() => {}}
                setOpenAdvancedFilters={setOpenAdvancedFilters}
                clear={onClear}
              />
            ) : (
              <JobsCrmSearch
                onSort={onSort}
                onSearch={(data) => search(data)}
                type={JobsCrm.PIPELINE}
                job={job}
                clearAdvanceFilters={() => onApplyFilters(pipelineEmptyValues, true)}
                handleDownload={handleDownload}
              />
            )}

            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              {type === 'submissions' ? (
                <Grid
                  container
                  item
                  xs={12}
                  px={2}
                  bgcolor={WsColors.White}
                  pb={1}
                  sx={{ border: '1px  solid #E0E0E0', borderTop: 'none' }}
                >
                  <ApplyedFilter
                    searchQuery={searchSubmissionQuery ?? []}
                    onDelete={(data) => onDeleteSubmissionFilterTag(data)}
                    getUpdatedFiltersFromAppliedFilters={(query) => setSubmissionQuery(query)}
                  />
                </Grid>
              ) : (
                <WsFilterTags filtersList={appliedFilters} onDelete={onDeleteFilterTag} />
              )}
            </ErrorBoundary>
          </Box>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <Box>
              <ScrollableTabs
                defaultValue={tabIndex}
                scrollList={pipelineTabsList(jobStats)}
                getTabIndex={(index) => ChangeIndex[index]()}
                variant={TabTextVariant.SUB_TITLE}
              />
            </Box>
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            <Box mt={1}>
              <WsPagination
                count={Math.ceil(
                  type === 'submissions' ? submissions?.totalPages : data.pagesCount
                )}
                onChange={pageChange}
                itemsPerPage={Number(paginationSettings.jobCrmPagination)}
                pageNumber={type === 'submissions' ? submissions?.number : query.page}
                defaultPage={pageNumber}
                isPageCount={true}
                type="Candidates"
                totalCount={
                  type === 'submissions' ? submissions?.totalElements : data.totalElements
                }
              />
            </Box>
          </ErrorBoundary>
          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            {(data.list.length > 0 || candidateList?.length > 0) && !loader ? (
              <Box>
                <Box>
                  {type === 'submissions' ? (
                    candidateList?.map((candidate, index) => (
                      <Box my={2} key={index}>
                        <WsCandidateCard
                          isCheckBox={true}
                          candidate={candidate}
                          gridXs={3}
                          isActiveCard={true}
                          openSingleEmail={() => {}}
                          onCVClick={onCVClick}
                          candidateIndex={index}
                          jobSource={job.jobSource}
                          checkBoxOnclick={() => addSelectedCandidate(candidate)}
                          stageStatus={{
                            applicationStage: candidate?.applicationStage,
                            applicationState: candidate?.applicationState,
                          }}
                          amplitudeCallBack={(eventType: string) => {
                            amplitudeEventJobCrmPipelineSubmissions[eventType](loggedInUser);
                          }}
                          array={candidateList.map((i) => i.candidateId ?? '')}
                          backwardpathArray={generateJobCrmBackwardURL(job.title)}
                        />
                      </Box>
                    ))
                  ) : (
                    <>
                      {data.list?.map((candidate, index) => (
                        <Box my={2} key={index}>
                          {/* {type === 'submissions' && (
                            <WsCandidateCard
                              isCheckBox={true}
                              candidate={candidate}
                              gridXs={3}
                              isActiveCard={true}
                              openSingleEmail={() => {}}
                              onCVClick={onCVClick}
                              candidateIndex={index}
                              jobSource={job.jobSource}
                              checkBoxOnclick={() => addSelectedCandidate(candidate)}
                              stageStatus={{
                                applicationStage: candidate?.applicationStage,
                                applicationState: candidate?.applicationState,
                              }}
                              amplitudeCallBack={(eventType: string) => {
                                amplitudeEventJobCrmPipelineSubmissions[eventType](loggedInUser);
                              }}
                              array={data.list.map((i) => i.candidateId ?? '')}
                            />
                          )} */}
                          {type === 'mappings' && (
                            <MappingCandidateCard
                              candidate={candidate}
                              downloadSource="JOBCRM_MAPPINGS"
                              isSelected={candidate.isSelected}
                              openSingleEmail={() => {}}
                              onCVClick={onCVClick}
                              candidateIndex={index}
                              job={job}
                              checkBoxOnclick={() => addSelectedCandidate(candidate)}
                              interaction={getInteraction(data.stageStatus, candidate.email)}
                              amplitudeEventHandler={(eventType: string) => {
                                amplitudeEventJobCrmPipelineMappings[eventType](loggedInUser);
                              }}
                              array={data.list.map((i) => i.candidateId ?? '')}
                              backwardpathArray={generateJobCrmBackwardURL(job.title)}
                            />
                          )}
                          {type === 'Applications' && (
                            <TalentPoolCandidateCard
                              candidate={candidate}
                              isSelected={candidate.isSelected}
                              openSingleEmail={() => {}}
                              onCVClick={onCVClick}
                              candidateIndex={index}
                              job={job}
                              checkBoxOnclick={() => addSelectedCandidate(candidate)}
                              isCvViewed={cvViewsData.keys?.includes(
                                (candidate.email || candidate?.linkedInURL) ?? ''
                              )}
                              amplitudeEventHandler={(eventType: string) =>
                                amplitudeEventJobCrmPipelineApplication[eventType](loggedInUser)
                              }
                              array={data.list.map((i) => i.candidateId ?? '')}
                              backwardpathArray={generateJobCrmBackwardURL(job.title)}
                            />
                          )}
                          {type === 'uploaded' && (
                            <TalentPoolCandidateCard
                              candidate={candidate}
                              isSelected={candidate.isSelected}
                              openSingleEmail={() => {}}
                              onCVClick={onCVClick}
                              candidateIndex={index}
                              job={job}
                              checkBoxOnclick={() => addSelectedCandidate(candidate)}
                              amplitudeEventHandler={(eventType: string) => {
                                amplitudeEventJobCrmPipelineUploaded[eventType](loggedInUser);
                              }}
                              array={data.list.map((i) => i.candidateId ?? '')}
                              backwardpathArray={generateJobCrmBackwardURL(job.title)}
                            />
                          )}
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
                <Grid item xs={12} container justifyContent="flex-end" my={2}>
                  <WsPagination
                    count={Math.ceil(
                      type === 'submissions' ? submissions?.totalPages : data.pagesCount
                    )}
                    onChange={pageChange}
                    itemsPerPage={Number(paginationSettings.jobCrmPagination)}
                    pageNumber={type === 'submissions' ? submissions?.number : query.page}
                    defaultPage={pageNumber}
                    isPageCount={false}
                    type="Candidates"
                    totalCount={
                      type === 'submissions' ? submissions?.totalElements : data.totalElements
                    }
                  />
                </Grid>
              </Box>
            ) : loader ? (
              <WsListingLoader />
            ) : (
              <NoDataFoundCard text="No Candidates Found" />
            )}
          </ErrorBoundary>
        </Grid>
        <Grid item xs={2.8}>
          {type === 'submissions' ? (
            <AdvancedFilters
              onClose={() => {}}
              defaultValues={advancedFilterDefaults}
              onSubmit={onFormSubmit}
            />
          ) : (
            <WsJobsCrmPipelineAdvanceFiltersNew
              defaultValues={advancedFilterDefaultValues}
              onApplyFilters={onApplyFilters}
            />
          )}
        </Grid>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {openShareCandidateEmail && (
            <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
              <ShareCandidateProfileEmail
                selectedCandidateEntitys={selectedCandidateEntitys}
                candidatesList={
                  candidateToShare != null
                    ? [candidateToShare]
                    : data.list.filter(
                        (candidate) =>
                          candidate.isSelected &&
                          !candidate.isInDnc &&
                          (candidate.downloadSource = 'BULK_SUBMISSION')
                      )
                }
                open={openShareCandidateEmail}
                handleClose={() => {
                  setOpenShareCandidateEmail(false);
                  unselectAllCandidates();
                }}
              />
            </ErrorBoundary>
          )}
        </ErrorBoundary>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {numberOfSelectedCandidates > 0 && (
            <CandidateBulkSelectionBar
              selectedCandidates={numberOfSelectedCandidates}
              selectAllCandidates={selectAllCandidates}
              unselectAllCandidates={unselectAllCandidates}
              openBulkSms={() => setOpenBulkSms(true)}
              openEmail={() => setOpenEmail(true)}
              amplitudeEventHandler={(eventType: string) => {
                amplitudeEventHandlerPipelineTabs[type](eventType);
              }}
              onShareEmailClick={type === 'submissions' ? onShareEmailClick : undefined}
            />
          )}
        </ErrorBoundary>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          <BulkSmsDrawer
            open={openBulkSms}
            handleClose={() => setOpenBulkSms(false)}
            candidates={data.list.filter(
              (candidate) =>
                candidate.isSelected && !candidate.isInDnc && !isEmpty(candidate.phoneNumber)
            )}
            defaultJob={job}
          />
        </ErrorBoundary>
        <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
          {openEmail && (
            <BulkEmail
              open={openEmail}
              ignoreDuplicateCheck={true}
              handleClose={() => setOpenEmail(false)}
              candidates={data.list.filter(
                (candidate) =>
                  candidate.isSelected &&
                  !candidate.isInDnc &&
                  (candidate.downloadSource = 'JOBCRM_MAPPINGS')
              )}
              drawer={true}
              defaultJob={job}
            />
          )}
        </ErrorBoundary>
      </Grid>
    </Box>
  );
};
export default JobsCrmPipeline;
